/**
 * AdminUsersPanel — admin-only user management, opened from the "Users" button
 * next to Logout on the main dashboard screen. Lists authorized users and lets
 * an admin invite new ones, change roles, and deactivate (remove) accounts.
 *
 * Rendered as a modal driven by the store's `adminUsersOpen` flag. Double-gated:
 * the button only shows for admins AND this returns null unless the signed-in
 * mapper is an admin; the backend enforces admin on every endpoint regardless.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { useAuthStore } from '../../store/authStore';
import {
  adminAddUser, adminListUsers, adminRemoveUser, adminSetApps, adminSetRole, type AdminUser,
} from '../../api/authClient';
import { color, font, ui } from '../ui/theme';
import { sectionStyle, resetBtnStyle, hintTextStyle, selectStyle } from './SettingsDialog.styles';

const ROLES = ['admin', 'mapper', 'external'] as const;

// The 4 apps a user may be granted (id matches Dashboard EDITORS `view`).
const APPS: [string, string][] = [
  ['synth-rider', 'Synth VR'],
  ['console', 'Synth Console'],
  ['flow', 'Flow'],
  ['roobloops', 'Roobloops'],
];

export default function AdminUsersPanel() {
  const open = useEditorStore(s => s.adminUsersOpen);
  const setOpen = useEditorStore(s => s.setAdminUsersOpen);
  const isAdmin = useAuthStore(s => s.mapper?.role === 'admin');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<typeof ROLES[number]>('external');

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      setUsers(await adminListUsers());
    } catch (e: any) {
      setErr(e?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load (and reload) the list whenever the modal opens.
  useEffect(() => { if (open && isAdmin) refresh(); }, [open, isAdmin, refresh]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr('');
    try {
      await fn();
      await refresh();
    } catch (e: any) {
      setErr(e?.message || 'Action failed.');
    } finally {
      // Always clear busy — previously it was reset only on error, so after a
      // SUCCESSFUL change every control stayed disabled and the panel froze.
      setBusy(false);
    }
  };

  // Toggle one app for a user. A null `apps` means default (all apps), so we
  // start from the full set and add/remove the toggled id, then persist.
  const toggleApp = (u: AdminUser, appId: string) => {
    const current = u.apps ?? APPS.map(([id]) => id);
    const next = current.includes(appId)
      ? current.filter(id => id !== appId)
      : [...current, appId];
    run(() => adminSetApps(u.email, next));
  };

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    run(async () => {
      await adminAddUser(email, inviteRole);
      setInviteEmail('');
      setInviteRole('external');
    });
  };

  if (!open || !isAdmin) return null;

  return (
    <div style={overlayStyle} onClick={() => setOpen(false)}>
    <div style={dialogStyle} onClick={e => e.stopPropagation()}>
      <div style={headerStyle}>
        <span style={{ fontWeight: 600, fontSize: 14, color: color.ink }}>Users</span>
        <button style={closeBtnStyle} onClick={() => setOpen(false)}>{'✕'}</button>
      </div>
      <div style={{ ...sectionStyle, overflowY: 'auto' }}>
      <div style={hintTextStyle}>
        Manage who can sign in. Invite by email, change roles, or deactivate accounts.
      </div>

      {/* Invite form */}
      <form onSubmit={invite} style={inviteRowStyle}>
        <input
          style={{ ...ui.input, flex: 1, minWidth: 0 }}
          type="email" placeholder="email to invite" value={inviteEmail}
          onChange={e => setInviteEmail(e.target.value)} autoComplete="off"
        />
        <select
          style={{ ...selectStyle, flex: '0 0 110px' }}
          value={inviteRole}
          onChange={e => setInviteRole(e.target.value as typeof ROLES[number])}
        >
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" style={{ ...ui.btn, padding: '8px 14px', fontSize: 11 }} disabled={busy || !inviteEmail.trim()}>
          Invite
        </button>
      </form>

      {err && <div style={errStyle}>{err}</div>}

      {/* User list */}
      {loading ? (
        <div style={{ ...hintTextStyle, marginTop: 8 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          <div style={headerRowStyle}>
            <span style={{ flex: 2 }}>Email</span>
            <span style={{ flex: '0 0 110px' }}>Role</span>
            <span style={{ flex: '0 0 70px', textAlign: 'center' }}>Status</span>
            <span style={{ flex: '0 0 80px' }} />
          </div>
          {users.map(u => {
            const isAdminUser = u.role === 'admin';
            return (
            <div key={u.email} style={userRowStyle}>
              <div style={userTopLineStyle}>
                <span style={{ flex: 2, color: color.ink, fontSize: 11, wordBreak: 'break-all' }}>{u.email}</span>
                <select
                  style={{ ...selectStyle, flex: '0 0 110px' }}
                  value={u.role}
                  disabled={busy}
                  onChange={e => run(() => adminSetRole(u.email, e.target.value))}
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <span style={{ flex: '0 0 70px', textAlign: 'center' }}>
                  <span style={u.active ? activeBadge : inactiveBadge}>
                    {u.active ? 'active' : 'off'}
                  </span>
                </span>
                <span style={{ flex: '0 0 80px', display: 'flex', justifyContent: 'flex-end' }}>
                  {u.active && (
                    <button
                      style={removeBtnStyle}
                      disabled={busy}
                      onClick={() => run(() => adminRemoveUser(u.email))}
                    >
                      Remove
                    </button>
                  )}
                </span>
              </div>
              {/* App entitlements. null apps = default all → every chip checked.
                  Admins always get all apps, so their chips are checked+disabled. */}
              <div style={appsRowStyle}>
                {APPS.map(([id, label]) => {
                  const checked = isAdminUser || (u.apps ? u.apps.includes(id) : true);
                  return (
                    <button
                      key={id}
                      type="button"
                      style={checked ? appChipOnStyle : appChipOffStyle}
                      disabled={busy || isAdminUser}
                      title={isAdminUser ? 'Admins always have all apps' : label}
                      onClick={() => toggleApp(u, id)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })}
          {users.length === 0 && (
            <div style={{ ...hintTextStyle, marginTop: 8 }}>No users yet.</div>
          )}
        </div>
      )}

      <button style={{ ...resetBtnStyle, color: color.accentSoft, background: 'transparent', borderColor: 'var(--line)' }} onClick={refresh} disabled={busy}>
        Refresh
      </button>
      </div>
    </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = { ...ui.overlay };
const dialogStyle: React.CSSProperties = {
  ...ui.dialog, width: '90%', maxWidth: 560, maxHeight: '75vh',
  display: 'flex', flexDirection: 'column',
};
const headerStyle: React.CSSProperties = { ...ui.dialogHeader, padding: '12px 16px' };
const closeBtnStyle: React.CSSProperties = { ...ui.closeBtn };

const inviteRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
  fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  color: color.accentSoft, fontFamily: font.sans,
};

const userRowStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 8px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
  fontFamily: font.sans,
};

const userTopLineStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
};

const appsRowStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 5, paddingLeft: 2,
};

const appChipBaseStyle: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
  borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontFamily: font.sans,
  transition: 'border-color 0.15s ease, color 0.15s ease, background 0.15s ease',
};

const appChipOnStyle: React.CSSProperties = {
  ...appChipBaseStyle,
  color: 'var(--success)',
  background: 'oklch(0.7 0.15 150 / 0.12)',
  border: '1px solid oklch(0.7 0.15 150 / 0.35)',
};

const appChipOffStyle: React.CSSProperties = {
  ...appChipBaseStyle,
  color: color.inkFaint,
  background: 'oklch(0.4 0.02 280 / 0.2)',
  border: '1px solid var(--line)',
};

const activeBadge: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, color: 'var(--success)',
  background: 'oklch(0.7 0.15 150 / 0.12)', border: '1px solid oklch(0.7 0.15 150 / 0.3)',
  borderRadius: 6, padding: '2px 6px',
};

const inactiveBadge: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, color: color.inkFaint,
  background: 'oklch(0.4 0.02 280 / 0.2)', border: '1px solid var(--line)',
  borderRadius: 6, padding: '2px 6px',
};

const removeBtnStyle: React.CSSProperties = {
  ...ui.btnDanger, padding: '5px 10px', fontSize: 10, borderRadius: 6,
};

const errStyle: React.CSSProperties = {
  fontSize: 11, color: '#fca5a5', marginTop: 6,
  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: 8, padding: '8px 10px', fontFamily: font.sans,
};
